<template>
  <v-card :flat="isSettingsStyle">
    <v-card-text>
      <h3 class="subheading mb2">{{ $t('bankData') }}</h3>
      <v-form v-model="isFormValid">
        <SubscriptionBaseData
          :address-keys="addressKeys"
          :card="card"
          :error-messages="errorMessages"
        />
        <v-flex xs12>
          <h3 class="subheading my-2">{{ $t('paymentData') }}</h3>
        </v-flex>
        <v-text-field
          v-model="iban.account_holder_name"
          :error-messages="errorMessages.name"
          :label="$t('name')"
          :rules="[$rules.isRequired]"
          class="mx-3"
        />

        <div class="StripeElementGroup mx-3">
          <div id="iban-element">
            <!-- A Stripe Element will be inserted here. -->
          </div>
          <span class="highlight"/>
          <span class="bar"/>
          <div id="bank-name"/>
          <!-- Used to display form errors. -->
          <div
            id="error-message"
            role="alert"
            class="v-messages red--text text--darken-1"/>
        </div>
        <v-checkbox
          v-model="acceptedParagraph"
          :label="$t('acceptParagraph')"
          class="mx-3"/>
      </v-form>
    </v-card-text>

    <v-card-text/>

    <v-card-actions>
      <v-spacer/>
      <v-btn
        flat
        @click="$emit('changePaymentMethod')"
      >
        {{ $t('choosePaymentMethod') }}
      </v-btn>
      <v-btn
        :disabled="!acceptedParagraph || !isCompleted || !isFormValid"
        :loading="isLoading"
        color="primary"
        @click="next"
      >
        {{ $t(isSettingsStyle ? 'updatePaymentType' : 'next') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import SubscriptionBaseData from '@/components/routes/subscription/SubscriptionBaseData'

import style from './stripeStyle'

export default {
  name: 'SubscriptionIbanElement',
  components: { SubscriptionBaseData },
  props: ['stripe', 'isSettingsStyle', 'isLoading'],
  data () {
    return {
      isCompleted: false,
      isFormValid: false,
      ibanElement: null,
      acceptedParagraph: false,
      iban: {
        currency: 'EUR',
        account_holder_name: ''
      },
      errorMessages: {},
      card: {
        name: '',
        address_line1: '',
        address_line2: '',
        address_city: '',
        address_state: '',
        address_zip: '',
        address_country: this.$t('country'),
        company_name: ''
      },
      addressKeys: [
        'company_name',
        'name',
        'address_line1',
        'address_line2',
        'address_state',
        'address_zip',
        'address_city',
        'address_country'
      ]
    }
  },
  computed: {
    ...mapGetters('user', ['getUser'])
  },
  methods: {
    next () {
      const data = Object.assign({}, this.iban, this.card)
      this.$emit('complete', { element: this.ibanElement, data })
    },
    fillUpBillingData () {
      const billingData = Object.assign({}, this.getUser.billing_data)

      if (this.getUser.billing_data) {
        billingData.name = this.getUser.billing_data.name
        billingData.company_name = this.getUser.billing_data.company_name
      } else {
        billingData.name = `${this.getUser.first_name} ${this.getUser.last_name}`
        billingData.company_name = this.getUser.company_data ? this.getUser.company_data.name : ''
      }

      billingData.address_line2 = billingData.address_line2 || ''

      Object.assign(this.card, billingData)
    }

  },
  mounted () {
    const elements = this.stripe.elements({ locale: 'auto' })
    this.ibanElement = elements.create('iban', { style, supportedCountries: ['SEPA'] })
    this.ibanElement.mount('#iban-element')

    const errorMessage = document.getElementById('error-message')
    const bankName = document.getElementById('bank-name')

    this.ibanElement.addEventListener('change', event => {
      this.isCompleted = event.complete

      errorMessage.textContent = event.error ? event.error.message : ''
      bankName.textContent = event.bankName ? event.bankName : ''
    })
  },
  created () {
    this.iban.account_holder_name = `${this.getUser.first_name} ${this.getUser.last_name}`
    this.fillUpBillingData()
  },
  i18n: {
    messages: {
      de: {
        country: 'Deutschland',
        paymentData: 'Bezahlinformationen',
        name: 'Besitzer',
        accountHolderType: 'Kontonutzung',
        individual: 'Privat',
        company: 'Geschäftlich',
        bankData: 'Bankdaten',
        choosePaymentMethod: 'Zahlungsmethode wählen',
        updatePaymentType: 'Zahlungsart aktualisieren',
        acceptParagraph: 'Ich ermächtige (A) EC Logistik Gesellschaft mbH, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich (B) weise ich mein Kreditinstitut an, die von EC Logistik Gesellschaft mbH auf mein gezogenen Lastschriften einzulösen.',
        next: 'Weiter'
      },
      en: {
        country: 'Germany',
        paymentData: 'Payment information',
        name: 'Name',
        accountHolderType: 'Account usage',
        individual: 'private',
        company: 'company',
        bankData: 'Bank data',
        choosePaymentMethod: 'Payment method',
        updatePaymentType: 'Update payment type',
        acceptParagraph: 'Ich ermächtige (A) EC Logistik Gesellschaft mbH, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich (B) weise ich mein Kreditinstitut an, die von EC Logistik Gesellschaft mbH auf mein gezogenen Lastschriften einzulösen.',
        next: 'Next'
      },
      ru: {
        country: 'Almanya',
        paymentData: 'Ödeme bilgisi',
        name: 'Имя',
        accountHolderType: 'Account usage',
        individual: 'Частный',
        company: 'Компания',
        bankData: 'Банковские данные',
        choosePaymentMethod: 'Способ оплаты',
        updatePaymentType: 'Обновить способ оплаты',
        acceptParagraph: 'Ich ermächtige (A) EC Logistik Gesellschaft mbH, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich (B) weise ich mein Kreditinstitut an, die von EC Logistik Gesellschaft mbH auf mein gezogenen Lastschriften einzulösen.'
      },
      tr: {
        country: 'Германия',
        paymentData: 'Информация об оплате',
        name: 'İsim',
        accountHolderType: 'Hesap kullanımı',
        individual: 'özel',
        company: 'şirket',
        bankData: 'Banka verisi',
        choosePaymentMethod: 'Ödeme tipi',
        updatePaymentType: 'Ödeme tipini güncelle',
        acceptParagraph: 'Ich ermächtige (A) EC Logistik Gesellschaft mbH, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich (B) weise ich mein Kreditinstitut an, die von EC Logistik Gesellschaft mbH auf mein gezogenen Lastschriften einzulösen.'
      }
    }
  }
}
</script>
